import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

export type MediaFile = {
  id: string
  key: string
  url: string
  filename: string
  mime_type: string
  size: number
  width: number | null
  height: number | null
  alt: string | null
  uploaded_by: string | null
  created_at: string
}
export type FileUsage = { type: string; title: string }

export const MAX_UPLOAD = 20 * 1024 * 1024
export const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/avif,application/pdf,video/mp4,video/quicktime,video/webm"
export const isImage = (f: Pick<MediaFile, "mime_type">) => f.mime_type.startsWith("image/")
export const fileSize = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

/** Uploads one file (raw bytes; the server checks the real type and the 20 MB cap). */
export async function uploadFile(file: File): Promise<MediaFile> {
  if (file.size > MAX_UPLOAD) throw new Error(`${file.name} is over 20 MB`)
  const base = import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin
  const res = await fetch(`${base}/admin/terify/files?filename=${encodeURIComponent(file.name)}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/octet-stream" },
    body: file,
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) throw new Error(body?.message ?? "Upload failed")
  return body.file as MediaFile
}

/** Uploads in order; returns what succeeded and what failed (with the reason). */
export async function uploadMany(files: File[]) {
  const done: MediaFile[] = []
  const failed: { name: string; error: string }[] = []
  for (const f of files) {
    try {
      done.push(await uploadFile(f))
    } catch (e) {
      failed.push({ name: f.name, error: (e as Error).message })
    }
  }
  return { done, failed }
}

export const FORMATS = ["jpg", "png", "webp", "gif", "avif", "pdf", "mp4", "mov", "webm"] as const
export const formatOf = (f: Pick<MediaFile, "filename">) => (f.filename.split(".").pop() ?? "").toUpperCase()
export type FileQuery = { q?: string; type?: string; format?: string; alt?: string; size?: string; sort?: string; dir?: string; limit: number; offset: number }

export function useFiles(p: FileQuery) {
  return useQuery({
    queryKey: ["files", "list", p],
    placeholderData: keepPreviousData,
    queryFn: () =>
      sdk.client.fetch<{ files: MediaFile[]; count: number }>("/admin/terify/files", {
        query: { q: p.q?.trim() || undefined, type: p.type || undefined, format: p.format || undefined, alt: p.alt || undefined, size: p.size || undefined, sort: p.sort || undefined, dir: p.dir || undefined, limit: p.limit, offset: p.offset },
      }),
  })
}
export function useFileUsage(ids: string[]) {
  return useQuery({
    enabled: ids.length > 0,
    queryKey: ["files", "usage", ids],
    queryFn: () => sdk.client.fetch<{ usage: Record<string, FileUsage[]> }>("/admin/terify/file-usage", { query: { ids: ids.join(",") } }),
  })
}
export function useUpdateFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; alt?: string | null; filename?: string }) => sdk.client.fetch<{ file: MediaFile }>(`/admin/terify/files/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
  })
}
export function useDeleteFiles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      let failed = 0
      for (const id of ids) {
        try {
          await sdk.client.fetch(`/admin/terify/files/${id}`, { method: "DELETE" })
        } catch {
          failed++
        }
      }
      return { deleted: ids.length - failed, failed }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
  })
}
